export interface VehicleClaim {
  user_id: number;
  user_name: string;
  state: string;
  since: string;
}

/** Minimal assignee shape needed for driver resolution. */
export interface VehicleAssigneeLike {
  user_id?: number | string | null;
  first_name?: string | null;
  last_name?: string | null;
}

export interface VehicleLike {
  id?: string | number;
  external_id?: string | number;
  vehicle_id?: string | number;
  title?: string;
  assigned_to?: number | string | null;
  assigned_to_name?: string | null;
  assignees?: VehicleAssigneeLike[] | null;
  custom_fields?: Record<string, unknown> | null;
  /** Active allocation from vehicle_driver_allocations (SSOT) */
  allocation?: { driver_user_id?: number; driver_name?: string } | null;
}

export interface ResolvedDriver {
  userId: number | null;
  name: string | null;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
}

function getPrimaryAssignee(assignees: VehicleAssigneeLike[] | null | undefined): VehicleAssigneeLike | null {
  if (!Array.isArray(assignees)) return null;
  return assignees.find((assignee) => {
    const assigneeId = Number(assignee?.user_id || 0);
    return Number.isFinite(assigneeId) && assigneeId > 0;
  }) || null;
}

/**
 * Single Source of Truth for resolving the driver of a vehicle.
 *
 * Priority chain:
 *   1. vehicle.allocation.driver_user_id — from vehicle_driver_allocations (SSOT)
 *   2. vehicle.assigned_to               — legacy column, no longer synced for vehicles
 *                                           (kept as fallback for non-enriched data paths)
 *   3. primaryAssignee.user_id          — from work_item_assignees (also derived from allocations)
 *   4. vehicleClaim.user_id              — from live vehicle claim (Cartrack etc.)
 *
 * Do NOT use customFields.current_driver_id or customFields.assigned_driver_name
 * as these are stale duplicates that get out of sync.
 */
export function resolveVehicleDriver(
  vehicle: VehicleLike,
  vehicleClaim?: VehicleClaim | null,
): ResolvedDriver {
  const primaryAssignee = getPrimaryAssignee(vehicle.assignees);

  const userId = (() => {
    const allocationDriverId = toFiniteNumber(vehicle.allocation?.driver_user_id);
    if (allocationDriverId != null && allocationDriverId > 0) return allocationDriverId;

    const assignedToId = toFiniteNumber(vehicle.assigned_to);
    if (assignedToId != null && assignedToId > 0) return assignedToId;

    const assigneeDriverId = toFiniteNumber(primaryAssignee?.user_id);
    if (assigneeDriverId != null && assigneeDriverId > 0) return assigneeDriverId;

    const claimDriverId = toFiniteNumber(vehicleClaim?.user_id);
    if (claimDriverId != null && claimDriverId > 0) return claimDriverId;

    return null;
  })();

  const name = (() => {
    const allocationName = toNonEmptyString(vehicle.allocation?.driver_name);
    if (allocationName) return allocationName;

    const assigneeName = [primaryAssignee?.first_name, primaryAssignee?.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();

    return toNonEmptyString(assigneeName)
      || toNonEmptyString(vehicle.assigned_to_name)
      || toNonEmptyString(vehicleClaim?.user_name)
      || null;
  })();

  return { userId, name };
}

/**
 * Get the latest inspection date from a list of inspection entries.
 * Sorts descending by inspection_date and returns the first entry's date.
 */
export function getLatestInspectionDate(
  inspections: Array<{ inspection_date?: string | null }>,
): string | null {
  if (!Array.isArray(inspections) || inspections.length === 0) return null;

  const sorted = [...inspections].sort((a, b) => {
    const leftTime = new Date(a.inspection_date || 0).getTime();
    const rightTime = new Date(b.inspection_date || 0).getTime();
    return rightTime - leftTime;
  });

  return sorted[0]?.inspection_date ?? null;
}

/**
 * Check if any inspection in the list was completed today.
 */
export function hasInspectionToday(
  inspections: Array<{ inspection_date?: string | null }>,
): boolean {
  if (!Array.isArray(inspections) || inspections.length === 0) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return inspections.some((insp) => {
    const d = new Date(insp.inspection_date || '');
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  });
}
